import { FluentProvider } from '@fluentui/react-components';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { usePrefersDark } from '@/hooks/useMediaQuery';

import { paletteStorage } from './paletteStorage';
import { DEFAULT_PALETTE, resolvePalette, type PaletteSelection } from './palettes';
import { ThemeContext, type ThemeContextValue } from './themeContext';
import { themeStorage, type ThemeMode } from './themeStorage';
import { themesFor, type ResolvedTheme } from './themes';

interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * Owns the app's single FluentProvider.
 *
 * Exactly one FluentProvider is mounted, at the root. Nesting them re-declares
 * the entire CSS-custom-property block and is a well-known performance and
 * specificity trap. `applyStylesToPortals` defaults to true, which is what lets
 * Dialogs, Menus, Tooltips and the Toaster inherit the theme — leave it on.
 *
 * Two preferences live here: light/dark/system, and the primary colour. Both are
 * read in the useState initialiser rather than an effect, so a stored choice is
 * already applied on the first paint instead of flashing the default first.
 */
export function ThemeProvider({ children }: ThemeProviderProps) {
  const [mode, setModeState] = useState<ThemeMode>(() => themeStorage.get() ?? 'system');
  const [palette, setPaletteState] = useState<PaletteSelection>(
    () => paletteStorage.get() ?? DEFAULT_PALETTE,
  );
  const prefersDark = usePrefersDark();

  const resolved: ResolvedTheme = mode === 'system' ? (prefersDark ? 'dark' : 'light') : mode;

  const resolvedPalette = useMemo(() => resolvePalette(palette), [palette]);
  const theme = themesFor(resolvedPalette)[resolved];

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    themeStorage.set(next);
  }, []);

  const setPalette = useCallback((next: PaletteSelection) => {
    setPaletteState(next);
    paletteStorage.set(next);
  }, []);

  useEffect(() => {
    const root = document.documentElement;

    // A stable hook for tests and for any CSS that needs to escape Griffel.
    root.dataset.theme = resolved;

    // Without this, native scrollbars, form controls and the browser's own
    // chrome stay light on a dark page.
    let meta = document.querySelector<HTMLMetaElement>('meta[name="color-scheme"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'color-scheme';
      document.head.appendChild(meta);
    }
    meta.content = resolved;

    // Paint the body so overscroll doesn't reveal a white strip. Neutral, so it
    // only depends on light vs dark — but it is read off the active theme rather
    // than a static one so a palette that ever tints the neutrals stays honest.
    document.body.style.backgroundColor = theme.colorNeutralBackground2;

    // The root too: public/theme-boot.js painted it before this bundle loaded,
    // and left alone it would keep that first colour through a theme switch.
    root.style.backgroundColor = theme.colorNeutralBackground2;
    root.style.colorScheme = resolved;
  }, [resolved, theme]);

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, resolved, setMode, palette, resolvedPalette, setPalette }),
    [mode, resolved, setMode, palette, resolvedPalette, setPalette],
  );

  return (
    <ThemeContext.Provider value={value}>
      <FluentProvider theme={theme}>{children}</FluentProvider>
    </ThemeContext.Provider>
  );
}
